import {
    Box,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import SimpleDialog from '../../common/SimpleDialog';
import { CustomerDetail } from './CustomerDetailsTable';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import useOptions from '../../../hooks/useOptions';
import { client } from '../../../utils/axios-client';
import { ApiResponseType } from '../../../types';
import ApplicationStore from '../../../stores/ApplicationStore';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import { Recorder } from 'vmsg';
import RecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { v4 as uuid } from 'uuid';
import EmailSettings from '../../../v2/features/Profile/EmailSettings/EmailSettings';
import SectionAppBar from '../../../v2/components/Section/SectionAppBar';
import StandardButton from '../../../v2/components/Button/StandardButton';
import SaveButton from '../../../v2/components/Button/SaveButton';
import ContentEditor from '../../../v2/components/ContentEditor/ContentEditor';
import { TokenType } from '../../../v2/components/ContentEditor/types/content-editor-types';

type Props = {
    open: boolean;
    onClose: () => void;
    record: CustomerDetail;
    selectedCustomerIds: string[];
};

const recorder = new Recorder({
    wasmURL: '/js/vmsg.wasm',
});

const EditCustomerDetails = ({ open, onClose, record, selectedCustomerIds }: Props) => {
    const [data, setData] = useState<CustomerDetail>({} as CustomerDetail);
    const [loading, setLoading] = useState(true);
    const { options: customers } = useOptions('CUSTOMERS');
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [recordings, setRecorings] = useState<any>();
    const audioRef = useRef<HTMLAudioElement | any>();

    useEffect(() => {
        setData(record);
        setLoading(false);
    }, [record]);

    const onSave = async () => {
        let hasError = false;
        const temp = cloneDeep(data);

        if (isEmpty(temp.customerId)) {
            hasError = true;
            temp.customerIdError = true;
        } else {
            temp.customerIdError = false;
        }

        if (temp.emailType === 'GMAIL' || temp.emailType === 'SMTP') {
            if (isEmpty(temp.emailAddress)) {
                hasError = true;
                temp.emailAddressError = true;
            } else {
                temp.emailAddressError = false;
            }
        }

        if (temp.emailType === 'SMTP') {
            if (isEmpty(temp.emailUserAccount)) {
                hasError = true;
                temp.emailUserAccountError = true;
            } else {
                temp.emailUserAccountError = false;
            }

            if (isEmpty(temp.smtpOutgoingHost)) {
                hasError = true;
                temp.smtpOutgoingHostError = true;
            } else {
                temp.smtpOutgoingHostError = false;
            }

            if (isEmpty(temp.smtpOutgoingPort)) {
                hasError = true;
                temp.smtpOutgoingPortError = true;
            } else {
                temp.smtpOutgoingPortError = false;
            }

            if (isEmpty(temp.smtpPassword)) {
                hasError = true;
                temp.smtpPasswordError = true;
            } else {
                temp.smtpPasswordError = false;
            }
        }

        if (temp.kpimi < 0) {
            hasError = true;
            temp.kpimiError = true;
        } else {
            temp.kpimiError = false;
        }

        if (temp.kpimql < 0) {
            hasError = true;
            temp.kpimqlError = true;
        } else {
            temp.kpimqlError = false;
        }

        if (temp.kpidc < 0) {
            hasError = true;
            temp.kpidcError = true;
        } else {
            temp.kpidcError = false;
        }

        if (temp.kpiOpportunity < 0) {
            hasError = true;
            temp.kpiOpportunityError = true;
        } else {
            temp.kpiOpportunityError = false;
        }

        if (temp.kpiActivities < 0) {
            hasError = true;
            temp.kpiActivitiesError = true;
        } else {
            temp.kpiActivitiesError = false;
        }

        //if (isEmpty(temp.emailSignature)) {
        //    hasError = true;
        //    temp.emailSignatureError = true;
        //} else {
        //    temp.emailSignatureError = false;
        //}

        if (hasError) {
            setData(temp);
            return;
        }

        const response = await client.post<ApiResponseType>(`/api/admin/users/customer-details`, data);

        if (response.data.succeeded) {
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Settings Saved Successfully';
                s.snackbarOpen = true;
            });

            onClose();
        } else {
            alert('error saving settings');
        }
    };

    const onUpdateInt = async (prop: string, value: string) => {
        const temp = cloneDeep(data);
        temp[prop] = parseInt(value);
        setData(temp);
    };

    const onUpdateString = async (prop: string, value: string) => {
        const temp = cloneDeep(data);
        temp[prop] = value;
        setData(temp);
    };

    const onUpdateBoolean = async (prop: string, value: boolean) => {
        const temp = cloneDeep(data);
        temp[prop] = value;
        setData(temp);
    };

    const onUpdateArray = async (prop: string, value: string[]) => {
        const temp = cloneDeep(data);
        temp[prop] = value;
        setData(temp);
    };

    const recordAudio = async () => {
        setIsLoading(true);

        if (isRecording) {
            const blob = await recorder.stopRecording();

            setIsLoading(false);
            setIsRecording(false);
            setRecorings(URL.createObjectURL(blob));

            const host = window.location.protocol + '//' + window.location.host;
            const newId = uuid();

            const form = new FormData();
            form.append('files', blob, 'record.mp3');
            form.append('phoneNumbersId', newId);
            const response2 = await client.post('/api/admin/users/phone-numbers/voicemail', form);

            setData({ ...data, outgoingVoiceMessageUrl: `/api/admin/users/phone-numbers/voicemail/${newId}` });
        } else {
            try {
                await recorder.initAudio();
                await recorder.initWorker();
                recorder.startRecording();
                setIsLoading(false);
                setIsRecording(true);
            } catch (e) {
                console.error(e);
                setIsLoading(false);
            }
        }
    };

    const playMP3 = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                setIsPlaying(false);
            } else {
                audioRef.current.play();
                setIsPlaying(true);
            }
        }
    };

    if (loading || customers.length === 0) return null;

    return (
        <SimpleDialog open={open} onClose={onClose} size="lg" fullHeight={false} padding={0}>
            <SectionAppBar
                title="Edit Settings"
                subTitle="Email Signature"
                buttons={
                    <>
                        <StandardButton
                            variant="contained"
                            size="small"
                            icon={<CloseIcon />}
                            style={{ color: 'white' }}
                            onClick={onClose}
                        >
                            Close
                        </StandardButton>
                        <SaveButton
                            variant="contained"
                            size="small"
                            icon={<SaveIcon />}
                            style={{ color: 'white' }}
                            onClick={onSave}
                        >
                            Save
                        </SaveButton>
                    </>
                }
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4, margin: 5 }}>
                <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 8, flexBias: 0, flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4, minWidth: 350 }}>
                            <Box>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="customer-label">Customer</InputLabel>
                                    <Select
                                        error={data.customerIdError}
                                        label="Customer"
                                        labelId="customer-label"
                                        fullWidth
                                        value={data.customerId}
                                        defaultValue={data.customerId}
                                        onChange={(e) => onUpdateString('customerId', e.target.value)}
                                    >
                                        {customers
                                            .filter(
                                                (item) =>
                                                    item.value === data.customerId ||
                                                    !selectedCustomerIds.some((c) => c === item.value),
                                            )
                                            .map((item) => (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flexBias: 0, flexGrow: 1 }}>
                                <EmailSettings data={data} onChange={setData} />
                            </Box>
                            <RenderIf claimType={ClaimType.UserProfile} claimValue={ClaimValue.SetUserProfileKPIs}>
                                <Card sx={{ minWidth: 275 }}>
                                    <CardContent sx={{ padding: 0 }}>
                                        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                                            Monthly KPIs
                                        </Typography>
                                        <Box sx={{ mb: 4 }}>
                                            <TextField
                                                error={data.kpiActivitiesError}
                                                fullWidth
                                                label="Activities"
                                                placeholder="Activities"
                                                size="small"
                                                type="number"
                                                value={data.kpiActivities}
                                                onChange={(e) => onUpdateInt('kpiActivities', e.target.value)}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 4 }}>
                                            <TextField
                                                error={data.kpimiError}
                                                fullWidth
                                                label="MI"
                                                placeholder="MI"
                                                size="small"
                                                type="number"
                                                value={data.kpimi}
                                                onChange={(e) => onUpdateInt('kpimi', e.target.value)}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 4 }}>
                                            <TextField
                                                error={data.kpimqlError}
                                                fullWidth
                                                label="MQL"
                                                placeholder="MQL"
                                                size="small"
                                                type="number"
                                                value={data.kpimql}
                                                onChange={(e) => onUpdateInt('kpimql', e.target.value)}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 4 }}>
                                            <TextField
                                                error={data.kpidcError}
                                                fullWidth
                                                label="DC"
                                                placeholder="DC"
                                                size="small"
                                                type="number"
                                                value={data.kpidc}
                                                onChange={(e) => onUpdateInt('kpidc', e.target.value)}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 4 }}>
                                            <TextField
                                                error={data.kpiOpportunityError}
                                                fullWidth
                                                label="Opportunity"
                                                placeholder="Opportunity"
                                                size="small"
                                                type="number"
                                                value={data.kpiOpportunity}
                                                onChange={(e) => onUpdateInt('kpiOpportunity', e.target.value)}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </RenderIf>
                        </Box>
                    </Box>
                    <Box sx={{ alignSelf: 'stretch' }}>
                        <ContentEditor
                            sx={{ alignSelf: 'stretch', height: '95%' }}
                            error={data.emailSignatureError}
                            html={data.emailSignature}
                            tokenType={TokenType.None}
                            onChange={(newValue) => onUpdateString('emailSignature', newValue)}
                        />
                        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 16, color: 'CornflowerBlue' }}>Outgoing Voicemail</Typography>
                            <StandardButton
                                disabled={isLoading}
                                onClick={recordAudio}
                                variant="outlined"
                                size="small"
                                icon={isRecording ? <StopIcon /> : <RecordIcon />}
                            >
                                {isRecording ? 'Stop Recording' : 'Start Recording'}
                            </StandardButton>
                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {data.outgoingVoiceMessageUrl && (
                                    <StandardButton
                                        variant="outlined"
                                        size="small"
                                        icon={isPlaying ? <StopIcon /> : <PlayArrowIcon />}
                                        onClick={playMP3}
                                    >
                                        {isPlaying ? 'Stop Playing' : 'Play Voicemail'}
                                    </StandardButton>
                                )}
                                {data.outgoingVoiceMessageUrl && (
                                    <li key={data.outgoingVoiceMessageUrl} style={{ display: 'none' }}>
                                        <audio controls ref={audioRef} onEnded={() => setIsPlaying(false)}>
                                            <source src={data.outgoingVoiceMessageUrl} type="audio/mpeg" />
                                        </audio>
                                    </li>
                                )}
                            </ul>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </SimpleDialog>
    );
};

export default EditCustomerDetails;
